import { ExtensionConceptSummary, PresentationTreeNetwork, PresentationTreeResponse, TaxonomyChild } from 'entities/tag';

export function mergeExtensionsIntoPresentationTree(
  rawTaxonomyData: PresentationTreeResponse | null | undefined,
  extensions: ExtensionConceptSummary[] | undefined,
  extensionPrefix: string | null
): { taxonomyData: PresentationTreeResponse | null | undefined; extensionConceptNames: string[] } {
  void extensionPrefix;

  if (!rawTaxonomyData || !extensions || extensions.length === 0) {
    return { taxonomyData: rawTaxonomyData, extensionConceptNames: [] };
  }

  // Group extensions by wider-anchor concept name.
  const byWiderAnchor = new Map<string, ExtensionConceptSummary[]>();
  for (const ext of extensions) {
    const widerName = ext.widerAnchor?.name;
    if (!widerName) continue;
    const existing = byWiderAnchor.get(widerName) ?? [];
    existing.push(ext);
    byWiderAnchor.set(widerName, existing);
  }

  const extensionConceptNames: string[] = extensions.map((e) => e.name);

  const toTreeNode = (ext: ExtensionConceptSummary): TaxonomyChild => ({
    id: ext.name,
    labels: ext.labels ?? {},
    isAbstract: false,
    conceptType: ext.conceptType,
    children: [],
    order: 999,
  });

  const visitChildren = (children: TaxonomyChild[]): TaxonomyChild[] =>
    children.map((child) => {
      const matches = byWiderAnchor.get(child.id);
      const mappedChildren = visitChildren(child.children ?? []);
      if (matches) {
        return { ...child, children: [...mappedChildren, ...matches.map(toTreeNode)] };
      }
      return { ...child, children: mappedChildren };
    });

  const presentationTree = rawTaxonomyData.presentationTree;
  const networks: PresentationTreeNetwork[] = presentationTree?.networks ?? [];
  const mergedNetworks: PresentationTreeNetwork[] = networks.map((network) => ({
    ...network,
    roots: (network.roots ?? []).map((root) => {
      const matches = byWiderAnchor.get(root.id);
      const mappedChildren = visitChildren(root.children ?? []);
      if (matches) {
        return { ...root, children: [...mappedChildren, ...matches.map(toTreeNode)] };
      }
      return { ...root, children: mappedChildren };
    }),
  }));

  return {
    taxonomyData: {
      ...rawTaxonomyData,
      presentationTree: presentationTree ? { ...presentationTree, networks: mergedNetworks } : presentationTree,
    },
    extensionConceptNames,
  };
}
